import { GoogleAnalyticsService } from '../../user/google-analytics.service';

/**
 * What was bought: a subscription plan or a one-off report. Each kind keeps
 * its own records, so a report bought while a subscription purchase is still
 * pending cannot overwrite it.
 */
export type PurchaseKind = 'subscription' | 'report';

/**
 * Per kind: the last checkout session gtag has confirmed it sent, and a
 * purchase this browser knows about but has no confirmation was ever sent.
 *
 * localStorage rather than sessionStorage: the success URL is bookmarkable
 * and survives the round trip out to Stripe and back, so a duplicate can
 * easily arrive in a different session.
 *
 * The pending record exists because of the shape of the success pages.
 * Getting from a Stripe redirect to a hit leaving the browser means the
 * shell, the application JavaScript, lazy chunks, sometimes a backend round
 * trip for the amount, and gtag.js having loaded. That is seconds of cold
 * start on a page the buyer lands on with somewhere else in mind, and
 * anything they do first cancels it. Whatever is pending is a purchase that
 * really happened and may never have been reported, so any later page load
 * in this browser tries again - see replayPendingPurchaseConversions, wired
 * up in AppComponent.
 *
 * The subscription keys predate the report ones and keep their names, so
 * records already sitting in buyers' browsers are still found.
 */
const STORAGE_KEYS: Record<PurchaseKind, { sent: string; pending: string }> = {
  subscription: {
    sent: 'subscription_conversion_sent',
    pending: 'subscription_conversion_pending',
  },
  report: {
    sent: 'report_conversion_sent',
    pending: 'report_conversion_pending',
  },
};

/**
 * How long a pending record is worth replaying.
 *
 * An ad blocker means the confirmation callback never arrives, ever, so
 * without a ceiling the record would be re-fired on every page load until
 * the end of time. A week is long past the point where the server-side
 * upload is the right answer instead.
 */
export const PENDING_CONVERSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface PendingPurchaseConversion {
  sessionId: string | null;
  /**
   * The subscription plan, or the report type. Named `plan` because records
   * in that shape are already stored in browsers.
   */
  plan: string;
  /** In major units (dollars), or null if it is not known. */
  value: number | null;
  currency: string | null;
  /** Date.now() when the purchase was first seen. */
  at: number;
}

export function readPendingConversion(
  kind: PurchaseKind
): PendingPurchaseConversion | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS[kind].pending);
    if (!raw) return null;

    const pending = JSON.parse(raw) as PendingPurchaseConversion;
    // Hand-edited, half-written or from an older shape: a bad record must
    // read as "nothing pending" rather than throw on a page it has no
    // business being involved in.
    if (typeof pending?.at !== 'number' || typeof pending?.plan !== 'string')
      return null;

    return pending;
  } catch {
    return null;
  }
}

export function writePendingConversion(
  kind: PurchaseKind,
  pending: PendingPurchaseConversion
): void {
  try {
    localStorage.setItem(STORAGE_KEYS[kind].pending, JSON.stringify(pending));
  } catch {
    // Storage denied (private mode, full quota). The conversion is still
    // reported from this page; it just cannot be recovered if that fails.
  }
}

export function clearPendingConversion(kind: PurchaseKind): void {
  try {
    localStorage.removeItem(STORAGE_KEYS[kind].pending);
  } catch {
    // As above.
  }
}

export function conversionAlreadySent(
  kind: PurchaseKind,
  sessionId: string
): boolean {
  try {
    return localStorage.getItem(STORAGE_KEYS[kind].sent) === sessionId;
  } catch {
    return false;
  }
}

/**
 * Called from gtag's `event_callback`, so it means the hit is on the wire
 * and not merely queued. That distinction is the whole point: an earlier
 * version marked the session done before the amount lookup had even
 * returned, which turned a closed tab into a purchase that was recorded as
 * counted and never was, unrecoverably, because every later visit then
 * returned early on that same mark.
 */
export function markConversionSent(
  kind: PurchaseKind,
  sessionId: string | null
): void {
  clearPendingConversion(kind);
  if (!sessionId) return;

  try {
    localStorage.setItem(STORAGE_KEYS[kind].sent, sessionId);
  } catch {
    // Storage denied. transaction_id still deduplicates inside Ads.
  }
}

/** Hands one purchase to gtag as its kind's conversion. */
export function sendPurchaseConversion(
  ga: GoogleAnalyticsService,
  kind: PurchaseKind,
  purchase: PendingPurchaseConversion
): void {
  const onSent = () => markConversionSent(kind, purchase.sessionId);
  const { plan, sessionId, value, currency } = purchase;

  if (kind === 'subscription') {
    ga.logSubscriptionPurchase(plan, sessionId, value, currency, onSent);
  } else {
    ga.logReportPurchase(plan, sessionId, value, currency, onSent);
  }
}

/**
 * Re-reports purchases this browser never got confirmation of sending.
 *
 * Called once per app start from AppComponent. In practice that makes "the
 * next page load" seconds away rather than never: a buyer who navigated off
 * the success page before the hit got out is, by definition, loading
 * another one.
 *
 * Safe to fire against a hit that did get out: both events carry the Stripe
 * session as `transaction_id`, which is what Ads and GA4 deduplicate on, and
 * a double count is the risk this whole file deliberately prefers to a
 * silent loss.
 */
export function replayPendingPurchaseConversions(
  ga: GoogleAnalyticsService
): void {
  for (const kind of Object.keys(STORAGE_KEYS) as PurchaseKind[]) {
    const pending = readPendingConversion(kind);
    if (!pending) continue;

    if (pending.sessionId && conversionAlreadySent(kind, pending.sessionId)) {
      clearPendingConversion(kind);
      continue;
    }

    if (Date.now() - pending.at > PENDING_CONVERSION_TTL_MS) {
      clearPendingConversion(kind);
      continue;
    }

    // Tracking must never break the page this happens to run on, and this
    // one runs on all of them.
    try {
      sendPurchaseConversion(ga, kind, pending);
    } catch (err) {
      console.error(`[${kind}-conversion] replay failed`, err);
    }
  }
}
